import { Box, Button, Divider, Group, Stack, Text } from '@mantine/core';
import { Panel } from 'components/Panel';
import { i18nKeys, useTranslation } from 'locales';
import { Fragment, type JSX, useState } from 'react';
import { useParams } from 'react-router';
import { useCloseThirdPartyCase } from 'shared/hooks/use-close-thirdparty-case';
import { useLoadThirdPartyCase } from 'shared/hooks/use-load-third-party-case';
import { invoiceActions } from 'store/invoice/invoice.actions';

export function ActionsPanel() {
  const { id } = useParams<{ id: string }>();
  const caseId = Number(id);
  const { t } = useTranslation();
  const [isConfirming, setIsConfirming] = useState(false);
  const { thirdPartyCase } = useLoadThirdPartyCase(caseId);
  const { closeThirdPartyCase } = useCloseThirdPartyCase();

  if (!thirdPartyCase) return null;

  const canClose =
    thirdPartyCase.caseType === 'lawyer' &&
    !thirdPartyCase.closed &&
    (thirdPartyCase.followedUp ?? false);

  const hasLetter = thirdPartyCase.invoiceFeedback?.hasTpCasePDF ?? false;

  if (!hasLetter && !canClose) return null;

  const handleClose = () => {
    closeThirdPartyCase({ id: caseId }, { onSuccess: () => setIsConfirming(false) });
  };

  const downloadLetter = () => {
    invoiceActions.document({
      id: thirdPartyCase.invoiceId,
      document_type: 'lawyer_case_pdf',
      third_party_case_id: caseId,
    });
  };

  const sections = [
    hasLetter && {
      key: 'letter',
      content: (
        <Group justify="space-between" align="center" py="sm">
          <Box>
            <Text size="sm" fw={600}>
              {t(i18nKeys.THIRD_PARTY_CASES.ACTIONS.SENT_LETTER)}
            </Text>
            <Text size="xs" c="dimmed">
              {t(i18nKeys.THIRD_PARTY_CASES.ACTIONS.DOWNLOAD_DESCRIPTION)}
            </Text>
          </Box>
          <Button variant="default" size="sm" onClick={downloadLetter}>
            {t(i18nKeys.DOWNLOAD)}
          </Button>
        </Group>
      ),
    },
    canClose && {
      key: 'close',
      content: isConfirming ? (
        <Stack gap="md" py="sm">
          <Box>
            <Text size="sm" fw={700} mb={4}>
              {t(i18nKeys.THIRD_PARTY_CASES.ACTIONS.CONFIRM_CLOSE)}
            </Text>
            <Text size="sm" c="dimmed">
              {t(i18nKeys.THIRD_PARTY_CASES.ACTIONS.CONFIRM_CLOSE_DESCRIPTION)}
            </Text>
          </Box>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setIsConfirming(false)}>
              {t(i18nKeys.CANCEL)}
            </Button>
            <Button color="red" onClick={handleClose}>
              {t(i18nKeys.THIRD_PARTY_CASES.ACTIONS.CONFIRM_CLOSE)}
            </Button>
          </Group>
        </Stack>
      ) : (
        <Group justify="space-between" align="center" py="sm">
          <Box>
            <Text size="sm" fw={600}>
              {t(i18nKeys.THIRD_PARTY_CASES.ACTIONS.CLOSE_CASE)}
            </Text>
            <Text size="xs" c="dimmed">
              {t(i18nKeys.THIRD_PARTY_CASES.ACTIONS.CLOSE_CASE_DESCRIPTION)}
            </Text>
          </Box>
          <Button color="red" variant="light" size="sm" onClick={() => setIsConfirming(true)}>
            {t(i18nKeys.THIRD_PARTY_CASES.ACTIONS.CLOSE)}
          </Button>
        </Group>
      ),
    },
  ].filter((section): section is { key: string; content: JSX.Element } => section !== false);

  return (
    <Panel title={t(i18nKeys.THIRD_PARTY_CASES.ACTIONS.TITLE)} withBorder>
      <Stack gap={0}>
        {sections.map(({ key, content }, index) => (
          <Fragment key={key}>
            {index > 0 && <Divider />}
            {content}
          </Fragment>
        ))}
      </Stack>
    </Panel>
  );
}
